/* eslint-disable @typescript-eslint/camelcase */
// SLS 日志契约使用 snake_case 字段名。
import type { SessionProduct } from '../routes/live/mock/types';

export type LiveEvent = 'page_view' | 'product_buy_click';
export type LivePage = 'home' | 'featured' | 'calendar' | 'search';
type Fields = Record<string, string | number | boolean | null | undefined>;

const ENDPOINT = 'https://daily-live-broadcast-trailer.cn-shenzhen.log.aliyuncs.com/logstores/daily-live-broadcast-trailer/track';
const ENVIRONMENT = process.env.PREACT_APP_ENV === 'production' ? 'production' : 'development';
let memoryVisitorId = '';

function makeId(): string {
  const random = new Uint32Array(2);
  if (window.crypto?.getRandomValues) window.crypto.getRandomValues(random);
  else { random[0] = Math.random() * 0xffffffff; random[1] = Math.random() * 0xffffffff; }
  return `live_${Date.now().toString(36)}_${random[0].toString(36)}${random[1].toString(36)}`;
}

function visitorId(): string {
  try {
    const stored = window.localStorage.getItem('unionId');
    if (stored) { memoryVisitorId = stored; return stored; }
    if (!memoryVisitorId) memoryVisitorId = makeId();
    window.localStorage.setItem('unionId', memoryVisitorId);
  } catch {
    // 禁用本地存储时，至少同一页面生命周期内保持访客一致。
    if (!memoryVisitorId) memoryVisitorId = makeId();
  }
  return memoryVisitorId;
}

function pageFromPath(): LivePage | '' {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  if (path === '/' || path === '/live') return 'home';
  if (path === '/live/featured') return 'featured';
  if (path === '/live/calendar') return 'calendar';
  if (path === '/live/search') return 'search';
  return '';
}

function browserEnvironment(): string {
  const ua = window.navigator.userAgent;
  if (/weibo/i.test(ua)) return 'weibo';
  if (/micromessenger/i.test(ua)) return 'wechat';
  if (/\bQQ\//i.test(ua)) return 'qq';
  return 'browser';
}

/** 尽量在跳转前发出；仅对网络/临时错误重试一次，复用原始事件 ID 供查询去重。 */
function send(body: string, retry = true): void {
  const retryOnce = (): void => {
    if (retry) window.setTimeout(() => send(body, false), 400);
  };
  try {
    const request = window.fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-log-apiversion': '0.6.0',
        'x-log-bodyrawsize': String(new Blob([body]).size),
      },
      body,
      keepalive: true,
      credentials: 'omit',
    });
    Promise.resolve(request).then((response) => {
      if (response.status === 408 || response.status === 429 || response.status >= 500) retryOnce();
    }).catch(retryOnce);
  } catch {
    retryOnce();
  }
}

/** 埋点失败不得阻断页面或购买。所有日志字段都是字符串，兼容 SLS Web Tracking。 */
export function trackLiveEvent(event: LiveEvent, fields: Fields = {}): string | undefined {
  try {
    if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
    const log: Record<string, string> = {};
    Object.keys(fields).forEach((key) => {
      const value = fields[key];
      if (value !== null && value !== undefined) log[key] = String(value);
    });
    const eventId = makeId();
    Object.assign(log, {
      event, event_id: eventId,
      app: 'daily-live-broadcast-trailer', sys: 'liveapp',
      is_test: String(ENVIRONMENT !== 'production' || /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)),
      time: String(Date.now()), visitor_id: visitorId(),
      page: String(fields.page ?? pageFromPath()), page_path: window.location.pathname,
      gh_id: (new URLSearchParams(window.location.search).get('gh_id') ?? '').trim(),
      browser: browserEnvironment(),
    });
    send(JSON.stringify({ __topic__: 'live_analytics_v1', __source__: 'daily-live-broadcast-trailer-h5', __logs__: [log] }));
    return eventId;
  } catch {
    return; // 包含 ID、环境、序列化和传输初始化失败。
  }
}

export function trackProductEvent(event: Exclude<LiveEvent, 'page_view'>, product: SessionProduct, fields: Fields = {}): string | undefined {
  return trackLiveEvent(event, {
    source_page: pageFromPath(),
    item_id: product.itemId, item_name: product.itemName,
    session_product_id: product.sessionProductId,
    session_id: product.session.id, session_date: product.session.date,
    anchor_id: product.session.anchorId, link_no: product.linkNo,
    ...fields,
  });
}
